import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { ShoppingBag, Check, Loader2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { db } from '../services/firebase';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';

export default function Merch() {
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [addedToCart, setAddedToCart] = useState({});

  useEffect(() => {
    if (!db) {
      setLoading(false);
      return;
    }

    const q = query(collection(db, 'merch'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const merchData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        // Map to expected format
        variants: {
          sizes: doc.data().sizes || [],
          colors: doc.data().colors || [],
        },
      }));
      setProducts(merchData);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const handleVariantChange = (productId, variantType, value) => {
    setSelectedVariants((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [variantType]: value,
      },
    }));
  };

  const handleAddToCart = (product) => {
    const variant = selectedVariants[product.id] || {};

    // Validate required variants
    if (product.variants?.sizes && !variant.size) {
      alert('Please select a size');
      return;
    }
    if (product.variants?.colors && !variant.color) {
      alert('Please select a color');
      return;
    }

    addToCart(product, variant);

    // Show feedback
    setAddedToCart((prev) => ({ ...prev, [product.id]: true }));
    setTimeout(() => {
      setAddedToCart((prev) => ({ ...prev, [product.id]: false }));
    }, 2000);
  };

  const formatPrice = (price) => {
    return `$${price.toFixed(2)}`;
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const categories = ['All', ...new Set(products.map(p => p.category).filter(Boolean))];
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredProducts = activeCategory === 'All'
    ? products
    : products.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero */}
      <motion.section
        className="py-32 px-6 bg-gradient-to-b from-[#0a0a0a] to-black"
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
      >
        <div className="container mx-auto text-center">
          <ShoppingBag className="w-16 h-16 text-orange-500 mx-auto mb-6" />
          <h1 className="text-6xl md:text-7xl font-bold mb-6">
            <span className="text-orange-500">Merch Store</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Official Scooby DaGodSon merchandise - Rep the DMV go-go culture
          </p>
        </div>
      </motion.section>

      {/* Category Filter */}
      <section className="py-8 px-6 bg-black border-b border-gray-800">
        <div className="container mx-auto">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 rounded-full font-semibold transition-all ${
                  activeCategory === category
                    ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Merch Grid */}
      <section className="py-20 px-6 bg-black">
        <div className="container mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <ShoppingBag className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No products available</h3>
              <p className="text-gray-400">Check back soon for new merchandise!</p>
            </div>
          ) : (
          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.1 }
              }
            }}
          >
            {filteredProducts.map((product) => (
              <motion.div
                key={product.id}
                className="bg-gradient-to-b from-[#1a1a1a] to-[#0f0f0f] rounded-xl overflow-hidden border border-gray-800 hover:border-orange-500/50 transition-all"
                variants={fadeInUp}
              >
                {/* Product Image */}
                <div className="relative aspect-square bg-gray-800 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23374151" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="24"%3E' + encodeURIComponent(product.name) + '%3C/text%3E%3C/svg%3E';
                    }}
                  />
                  {product.featured && (
                    <div className="absolute top-3 left-3 bg-gradient-to-r from-orange-500 to-red-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                      Featured
                    </div>
                  )}
                  {product.stock > 0 && (
                  <div className="absolute top-3 right-3 bg-black/80 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    {product.stock} in stock
                  </div>
                )}
                {!product.inStock && (
                  <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                    Sold Out
                  </div>
                )}
                </div>

                {/* Product Info */}
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2">{product.name}</h3>
                  <p className="text-gray-400 text-sm mb-4">{product.description}</p>
                  <p className="text-2xl font-bold text-orange-500 mb-4">
                    {formatPrice(product.price)}
                  </p>

                  {/* Size Selection */}
                  {product.variants?.sizes && (
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        Size
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {product.variants.sizes.map((size) => (
                          <button
                            key={size}
                            onClick={() => handleVariantChange(product.id, 'size', size)}
                            className={`px-4 py-2 rounded-lg border-2 font-semibold transition-all ${
                              selectedVariants[product.id]?.size === size
                                ? 'border-orange-500 bg-orange-500/20 text-orange-500'
                                : 'border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600'
                            }`}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Color Selection */}
                  {product.variants?.colors && (
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        Color
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {product.variants.colors.map((color) => (
                          <button
                            key={color.name}
                            onClick={() => handleVariantChange(product.id, 'color', color)}
                            className={`relative px-4 py-2 rounded-lg border-2 font-semibold transition-all ${
                              selectedVariants[product.id]?.color?.name === color.name
                                ? 'border-orange-500 bg-orange-500/20 text-orange-500'
                                : 'border-gray-700 bg-gray-800/50 text-gray-400 hover:border-gray-600'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <div
                                className="w-4 h-4 rounded-full border-2 border-gray-600"
                                style={{ backgroundColor: color.hex }}
                              />
                              {color.name}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add to Cart Button */}
                  <Button
                    onClick={() => handleAddToCart(product)}
                    disabled={!product.inStock || product.stock === 0}
                    className={`w-full font-semibold transition-all ${
                      addedToCart[product.id]
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700'
                    } text-white`}
                  >
                    {addedToCart[product.id] ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Added to Cart
                      </>
                    ) : (
                      <>
                        <ShoppingBag className="w-4 h-4 mr-2" />
                        Add to Cart
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            ))}
          </motion.div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <motion.section
        className="py-20 px-6 bg-[#0a0a0a]"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInUp}
      >
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold mb-6">Shipping & Returns</h2>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div>
              <div className="text-4xl mb-4">🚚</div>
              <h3 className="font-semibold mb-2">Free Shipping</h3>
              <p className="text-gray-400 text-sm">On orders over $50 within the US</p>
            </div>
            <div>
              <div className="text-4xl mb-4">↩️</div>
              <h3 className="font-semibold mb-2">Easy Returns</h3>
              <p className="text-gray-400 text-sm">30-day return policy on all items</p>
            </div>
            <div>
              <div className="text-4xl mb-4">✨</div>
              <h3 className="font-semibold mb-2">Quality Guaranteed</h3>
              <p className="text-gray-400 text-sm">Premium materials, authentic designs</p>
            </div>
          </div>
        </div>
      </motion.section>
    </div>
  );
}
